import type { Auth0ContextInterface, User } from '@auth0/auth0-react';
import useRisksmartUser from '@risksmart-app/components/src/hooks/useRisksmartUser';
import { getEnv } from '@risksmart-app/components/src/utils/environment';
import type { AppRouter } from '@risksmart-app/trpc/src/routers/router';
import { QueryClientProvider } from '@tanstack/react-query';
import { createTRPCClient, httpBatchLink, splitLink } from '@trpc/client';
import type { Context, FC, ReactNode } from 'react';
import { useEffect, useRef, useState } from 'react';
import superjson from 'superjson';

import {
  getQueryClient,
  TRPCProvider as TanstackTRPCProvider,
} from '../utils/trpc';
import { createTrpcAuthHeaders } from './trpcAuthHeaders';

interface Props {
  authContext?: Context<Auth0ContextInterface<User>>;
  children: ReactNode;
}
export const TrpcProvider: FC<Props> = ({ children, authContext }) => {
  const { getAccessTokenSilently, isLoading, isAuthenticated } =
    useRisksmartUser(authContext);

  const queryClient = getQueryClient();

  // Use refs so the headers function always reads current values
  // without recreating the tRPC client (which causes issues with React Query)
  const getTokenRef = useRef(getAccessTokenSilently);
  const isLoadingRef = useRef(isLoading);
  const isAuthenticatedRef = useRef(isAuthenticated);

  useEffect(() => {
    getTokenRef.current = getAccessTokenSilently;
    isLoadingRef.current = isLoading;
    isAuthenticatedRef.current = isAuthenticated;
  }, [getAccessTokenSilently, isLoading, isAuthenticated]);

  // Clear cache when auth state changes to prevent stale data from previous sessions
  useEffect(() => {
    if (!isLoading) {
      queryClient.clear();
    }
  }, [isAuthenticated, queryClient, isLoading]);

  const [trpcClient] = useState(() => {
    // Shared by both links below so the two can never drift — they differ only
    // in the HTTP method they use.
    const linkOptions = {
      url: `${getEnv('REACT_APP_TRPC_API_URL')}/trpc`,
      // Keep batching disabled (one HTTP request per procedure call) for
      // now — it's much easier to debug while we're going live (COI-451).
      // The stale-while-revalidate query cache (see utils/trpc.ts) is the
      // shipping half of this change; batching can be revisited later.
      maxItems: 1,
      transformer: superjson,
      // Aborts rather than sending an unauthenticated request, and drives
      // re-auth on a failed silent refresh (COI-897). Branch selection lives in
      // trpcAuthHeaders.ts so it is testable without React.
      headers: createTrpcAuthHeaders({
        isLoading: () => isLoadingRef.current,
        isAuthenticated: () => isAuthenticatedRef.current,
        getToken: () => getTokenRef.current(),
      }),
    };

    return createTRPCClient<AppRouter>({
      links: [
        splitLink({
          // Reporting inputs are unbounded — one `fields[]` entry per selected
          // column plus a recursive filter AST — and a tRPC query is a GET with
          // the superjson-encoded input in the query string, which they do not
          // fit in. `maxURLLength` is no help: `maxItems: 1` means the batch
          // cannot be split any further. So POST the reporting reads and leave
          // every other operation on GET (cacheable, unchanged).
          condition: (op) => op.path.startsWith('frontend.reporting.'),
          true: httpBatchLink({ ...linkOptions, methodOverride: 'POST' }),
          false: httpBatchLink(linkOptions),
        }),
      ],
    });
  });

  return (
    <QueryClientProvider client={queryClient}>
      <TanstackTRPCProvider trpcClient={trpcClient} queryClient={queryClient}>
        {children}
      </TanstackTRPCProvider>
    </QueryClientProvider>
  );
};
